import styles from './dropdown.module.scss';
import {
  FieldValues,
  useController,
  UseControllerProps
} from "react-hook-form";
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import arrowIcon from '../../images/arrow.svg';
import { Popup } from "../popup/popup";

export type DropdownItem = {
  id: number | string;
  name: string | JSX.Element | JSX.Element[];
  title?: string;
  disabled?: boolean;
}

type DropdownProps<T extends FieldValues> = {
  items: DropdownItem[];
  showCleanField?: boolean;
  disabled?: boolean;
  fontPopupClass?: string;
  selectedItemClass?: string;
  isOpenProp?: boolean;
  setIsOpenProp?: (isOpen: boolean) => void;
  error?: string;
} & UseControllerProps<T>

export const Dropdown = <T extends FieldValues>({ items, showCleanField, control, disabled, name, fontPopupClass, selectedItemClass, isOpenProp, setIsOpenProp, error }: DropdownProps<T>) => {
  const dropdownContainer = useRef<HTMLDivElement>(null);

  const [localItems, setLocalItems] = useState<Array<DropdownItem>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isOpen, setIsOpen] = useState<boolean>(!!isOpenProp);

  const {
    field: { value: currentValue, onChange },
  } = useController({
    name,
    control
  });

  useEffect(() => {
    const result = [...items];
    if (showCleanField) {
      result.unshift({
        id: 0,
        name: ""
      });
    }
    setLocalItems(result);
  }, [items, showCleanField]);

  const areValuesEqual = (a: unknown, b: unknown): boolean => {
    return a === b || (a?.toString() === b?.toString());
  };

  useEffect(() => {
    if (localItems.length !== 0) {
      const defaultIndex = localItems.findIndex(item => areValuesEqual(item.id, currentValue));
      if (defaultIndex > -1) {
        setSelectedIndex(defaultIndex);
        if (!areValuesEqual(currentValue, localItems[defaultIndex].id)) {
          onChange(localItems[defaultIndex].id);
        }
      } else {
        setSelectedIndex(0);
        onChange(localItems[0].id);
      }
    }
  }, [localItems, currentValue, onChange]);

  useEffect(() => {
    if (!!setIsOpenProp) {
      setIsOpenProp(isOpen);
    }
  }, [isOpen, setIsOpenProp]);

  useEffect(() => {
    setIsOpen(!!isOpenProp);
  }, [isOpenProp]);

  const onClickDropdown = (): void => {
    if (!isOpen && !disabled) {
      setIsOpen(true);
    }
  }

  const onClosePopup = (): void => {
    setIsOpen(false);
  }

  const onClickItem = (itemIndex: number): void => {
    if (!localItems[itemIndex].disabled) {
      if (selectedIndex !== itemIndex) {
        onChange(localItems[itemIndex].id);
        setSelectedIndex(itemIndex);
      }
      setIsOpen(false);
    }
  }

  const getItemClassName = (index: number): string => {
    let result = styles.item;

    if (index === selectedIndex) {
      result += ` ${styles.selected}`;
    }

    if (!!fontPopupClass) {
      result += ` ${fontPopupClass}`;
    }

    return result;
  }

  const classContainer = useMemo(() => {
    let result = styles.container;
    if (disabled) {
      result += ` ${styles.disabled}`
    }
    if (!!error) {
      result += ` ${styles.error}`
    }
    return result;
  }, [disabled, error]);

  return (
    <div className={classContainer}
         onClick={onClickDropdown}
         ref={dropdownContainer}>
      <div className={`${styles.selectedItem} ${!!selectedItemClass ? selectedItemClass : ""}`}
           title={localItems[selectedIndex]?.title}>
        {localItems[selectedIndex]?.name}
      </div>
      <img className={styles.arrow} src={arrowIcon} alt=""/>
      {error && <p className={styles.errorMessage}> {error}</p>}

      <Popup
        parent={dropdownContainer.current}
        width={dropdownContainer.current?.offsetWidth}
        isShow={isOpen}
        onClose={onClosePopup}
        child={
          <div className={styles.popupContainer}>
            <div className={styles.items}>
              {localItems.map((item, index) => (
                <div key={item.id}
                     className={getItemClassName(index)}
                     title={item.title}
                     onClick={() => onClickItem(index)}>
                  {item.name}
                </div>
              ))}
            </div>
          </div>
        }/>
    </div>
  )
}